import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  Columns3,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  HeartPulse,
  History,
  Inbox,
  Landmark,
  Loader2,
  LogOut,
  PieChart,
  Play,
  Printer,
  Receipt,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Sparkles,
  Table2,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";
import API from "../../services/api";
import { getApiErrorDetail } from "../../utils/idempotency";
import { AuthContext } from "../Context/AuthContext";
import { useToast } from "../Context/ToastContext";
import { useEmployees } from "../../hooks/useEmployees";
import PayrollRunSelect from "../../components/common/PayrollRunSelect";
import ReportsEnterpriseShell from "./components/ReportsEnterpriseShell";
import ReportsGuide from "./components/ReportsGuide";
import { useReportsNavigation } from "./useReportsNavigation";
import {
  buildPayrollRegisterRows,
  buildSalaryRegisterRows,
  buildBankTransferRows,
  buildLopReportRows,
  buildPfReportRows,
  buildTdsReportRows,
  buildPayrollSummaryRow,
  exportRowsToXlsx,
  exportRowsToPdf,
} from "./payrollReportsUtils";
import {
  applyDirectoryFilters,
  applyJoiningFilters,
  applyMasterFilters,
  buildExitReportRows,
  computeWorkforceKpis,
  directoryReportRows,
  employeeDepartmentId,
  exitReportExportRows,
  fetchAllEmployees,
  joiningReportRows,
  masterReportRows,
} from "./employeeReportsUtils";
import { downloadBlob } from "../Employees/employeeFileUtils";
import { EMPLOYEE_TYPES, normalizeEmployeeType } from "../Employees/employeeTypeConstants";
import {
  buildAbsentReportRows,
  buildEmployeeDeptMap,
  buildLateReportRows,
  buildMonthlySummaryRows,
} from "./attendanceReportsUtils";
import TdsPayrollReportPanel from "./TdsPayrollReportPanel";
import { printRowsAsTable } from "./reportPrint";
import "../../styles/payrollReportsWorkspace.css";

const PAGE_SIZE = 50;
const EXPORT_COUNT_KEY = "prw:exportCounts";
const THEME_KEY = "prw:theme";

const MONEY_RE =
  /salary|gross|deduction|net|contribution|\bpf\b|\besi\b|\bpt\b|tds|hra|basic|allowance|amount|income|taxable/i;
const COUNT_RE = /lop days|days|count|employees/i;

const parseNum = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export const PAYROLL_HUB_REPORTS = [
  {
    key: "employee_salary_register",
    name: "Employee Salary Register",
    category: "Payroll Reports",
    icon: Table2,
    description:
      "Enterprise monthly salary statement — full breakup per employee from frozen payroll snapshots.",
    needs: ["month"],
    href: "/reports/payroll/employee-salary-register",
    isLink: true,
  },
  {
    key: "payroll_register",
    name: "Payroll Register",
    category: "Payroll Reports",
    icon: FileSpreadsheet,
    description: "Summary of gross earnings, deductions, and net pay per employee.",
    needs: ["month"],
    build: ({ runId, departmentId, employees, scopeIds }) =>
      buildPayrollRegisterRows({ runId, departmentId, employees, scopeIds }),
    kpis: (rows) => [
      { label: "Employees", value: rows.length },
      { label: "Gross Salary", value: inr(rows.reduce((a, r) => a + parseNum(r.Gross), 0)) },
      { label: "Deductions", value: inr(rows.reduce((a, r) => a + parseNum(r.Deductions), 0)) },
      { label: "Net Salary", value: inr(rows.reduce((a, r) => a + parseNum(r.Net), 0)) },
    ],
  },
  {
    key: "salary_register",
    name: "Salary Register",
    category: "Payroll Reports",
    icon: Receipt,
    description: "Detailed salary breakup — Basic, HRA, allowances and statutory splits.",
    needs: ["month"],
    build: ({ runId, departmentId, employees, scopeIds }) =>
      buildSalaryRegisterRows({ runId, departmentId, employees, scopeIds }),
    kpis: (rows) => [
      { label: "Employees", value: rows.length },
      { label: "Net Salary", value: inr(rows.reduce((a, r) => a + parseNum(r["Net Salary"]), 0)) },
      { label: "Total PF", value: inr(rows.reduce((a, r) => a + parseNum(r.PF), 0)) },
      { label: "Total TDS", value: inr(rows.reduce((a, r) => a + parseNum(r.TDS), 0)) },
    ],
  },
  {
    key: "payroll_summary",
    name: "Payroll Summary",
    category: "Analytics Reports",
    icon: PieChart,
    description: "Single-row aggregate of the payroll run.",
    needs: ["month"],
    build: ({ runId, scopeIds }) => buildPayrollSummaryRow({ runId, scopeIds }),
    kpis: (rows) => {
      const r = rows[0] || {};
      return [
        { label: "Employees", value: r["Total Employees"] ?? 0 },
        { label: "Gross Salary", value: inr(parseNum(r["Gross Salary"])) },
        { label: "Deductions", value: inr(parseNum(r["Total Deductions"])) },
        { label: "Net Salary", value: inr(parseNum(r["Net Salary"])) },
      ];
    },
  },
  {
    key: "bank_transfer",
    name: "Bank Transfer Report",
    category: "Banking Reports",
    icon: Landmark,
    description: "Net salary with bank account and IFSC for disbursement.",
    needs: ["month"],
    build: ({ runId, employees, scopeIds }) => buildBankTransferRows({ runId, employees, mask: true, scopeIds }),
    buildExport: ({ runId, employees, scopeIds }) =>
      buildBankTransferRows({ runId, employees, mask: false, scopeIds }),
    kpis: (rows) => [
      { label: "Employees", value: rows.length },
      {
        label: "With Bank Details",
        value: rows.filter((r) => !/no bank details/i.test(r["Bank Account"] || "")).length,
      },
      { label: "Net Salary", value: inr(rows.reduce((a, r) => a + parseNum(r["Net Salary"]), 0)) },
    ],
  },
  {
    key: "lop",
    name: "LOP Report",
    category: "Payroll Reports",
    icon: UserMinus,
    description: "Loss-of-pay days and recovered amount per employee.",
    needs: ["month"],
    build: ({ runId, departmentId, employees, scopeIds }) =>
      buildLopReportRows({ runId, departmentId, employees, scopeIds }),
    kpis: (rows) => [
      { label: "Employees w/ LOP", value: rows.length },
      { label: "Total LOP Days", value: rows.reduce((a, r) => a + parseNum(r["LOP Days"]), 0) },
      { label: "LOP Amount", value: inr(rows.reduce((a, r) => a + parseNum(r["LOP Amount"]), 0)) },
    ],
  },
  {
    key: "pf",
    name: "PF Report",
    category: "Statutory Reports",
    icon: ShieldCheck,
    description: "Provident Fund — employee and employer contribution.",
    needs: ["month"],
    build: ({ runId, employees, scopeIds }) => buildPfReportRows({ runId, employees, scopeIds }),
    kpis: (rows) => [
      { label: "Covered Employees", value: rows.length },
      { label: "Employee PF", value: inr(rows.reduce((a, r) => a + parseNum(r["Employee Contribution"]), 0)) },
      { label: "Employer PF", value: inr(rows.reduce((a, r) => a + parseNum(r["Employer Contribution"]), 0)) },
    ],
  },
  {
    key: "esi",
    name: "ESI Report",
    category: "Statutory Reports",
    icon: HeartPulse,
    description: "Enterprise ESIC monthly contribution report — statutory columns, filters, and exports.",
    needs: ["month"],
    href: "/reports/esi",
    isLink: true,
  },
  {
    key: "tds",
    name: "TDS Report",
    category: "Statutory Reports",
    icon: FileText,
    description: "Taxable income and TDS deducted per employee.",
    needs: ["month"],
    build: ({ runId, employees, scopeIds }) => buildTdsReportRows({ runId, employees, scopeIds }),
    kpis: (rows) => [
      { label: "Employees", value: rows.length },
      { label: "Taxable Income", value: inr(rows.reduce((a, r) => a + parseNum(r["Taxable Income"]), 0)) },
      { label: "TDS Deducted", value: inr(rows.reduce((a, r) => a + parseNum(r["TDS Deducted"]), 0)) },
    ],
  },
  {
    key: "employee_master",
    name: "Employee Master",
    category: "Employee Reports",
    icon: Users,
    description: "Full workforce register — code, name, department, designation, DOJ, status.",
    needs: [],
    build: ({ departmentId, employees }) =>
      masterReportRows(applyMasterFilters(employees, { departmentId })),
    kpis: (rows, ctx) => {
      const k = computeWorkforceKpis(ctx.employees);
      return [
        { label: "Total Employees", value: k.total },
        { label: "Active", value: k.active },
        { label: "Inactive", value: k.inactive },
        { label: "New Joiners (month)", value: k.newJoiners },
      ];
    },
  },
  {
    key: "employee_directory",
    name: "Employee Directory",
    category: "Employee Reports",
    icon: Users,
    description: "Contact directory — mobile, email, department, designation.",
    needs: [],
    build: ({ departmentId, employees }) =>
      directoryReportRows(applyDirectoryFilters(employees, { departmentId })),
    kpis: (rows) => [{ label: "Employees", value: rows.length }],
  },
  {
    key: "joining_report",
    name: "Joining Report",
    category: "Employee Reports",
    icon: UserPlus,
    description: "Employees who joined in the selected calendar month.",
    needs: ["calMonth"],
    build: ({ departmentId, employees, fromDate, toDate }) =>
      joiningReportRows(applyJoiningFilters(employees, { departmentId, fromDate, toDate })),
    kpis: (rows) => [{ label: "New Joiners", value: rows.length }],
  },
  {
    key: "exit_report",
    name: "Exit Report",
    category: "Employee Reports",
    icon: LogOut,
    description: "Exits/offboardings whose last working day falls in the selected calendar month.",
    needs: ["calMonth"],
    build: async ({ fromDate, toDate, matchesEmployee }) =>
      exitReportExportRows(await buildExitReportRows({ fromDate, toDate, matchesEmployee })),
    kpis: (rows) => [{ label: "Exits", value: rows.length }],
  },
  {
    key: "attendance_summary",
    name: "Attendance Summary",
    category: "Attendance Reports",
    icon: CalendarDays,
    description: "Present / absent / leave / holiday day counts per employee for the calendar month.",
    needs: ["calMonth"],
    build: ({ calYear, calMonthNum, departmentId, scopeIds }) =>
      buildMonthlySummaryRows({ year: calYear, month: calMonthNum, departmentId, scopeIds }),
    kpis: (rows) => [
      { label: "Employees", value: rows.length },
      { label: "Present Days", value: rows.reduce((a, r) => a + parseNum(r.Present), 0) },
      { label: "Absent Days", value: rows.reduce((a, r) => a + parseNum(r.Absent), 0) },
      { label: "Leave Days", value: rows.reduce((a, r) => a + parseNum(r.Leave), 0) },
    ],
  },
  {
    key: "absent_report",
    name: "Absent Report",
    category: "Attendance Reports",
    icon: UserMinus,
    description: "Absence instances (employee + date) within the calendar month.",
    needs: ["calMonth"],
    build: ({ fromDate, toDate, departmentId, employeeDeptMap, scopeIds }) =>
      buildAbsentReportRows({ fromDate, toDate, departmentId, employeeDeptMap, scopeIds }),
    kpis: (rows) => [
      { label: "Absent Instances", value: rows.length },
      { label: "Employees Affected", value: new Set(rows.map((r) => r.Employee)).size },
    ],
  },
  {
    key: "late_coming",
    name: "Late Coming Report",
    category: "Attendance Reports",
    icon: Clock,
    description: "Late arrivals with actual in-time and minutes late, for the calendar month.",
    needs: ["calMonth"],
    build: ({ fromDate, toDate, departmentId, employeeDeptMap, scopeIds }) =>
      buildLateReportRows({ fromDate, toDate, departmentId, employeeDeptMap, scopeIds }),
    kpis: (rows) => [
      { label: "Late Instances", value: rows.length },
      { label: "Employees Affected", value: new Set(rows.map((r) => r.Employee)).size },
    ],
  },
];

const REPORTS = PAYROLL_HUB_REPORTS;

/** Payroll run statuses (keys from payrollRunUtils.resolveRunStatus). */
const RUN_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "completed", label: "Processed" },
  { value: "approved", label: "Approved" },
  { value: "verified", label: "Verified" },
  { value: "locked", label: "Locked" },
  { value: "partial", label: "Partial" },
  { value: "failed", label: "Failed" },
];

function employeeLocationId(emp) {
  const id = emp?.location_id ?? emp?.location?.location_id ?? emp?.location?.id ?? "";
  return id ? String(id) : "";
}


const currentCalMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function readJson(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
}

function bumpExportCount(reportKey) {
  const counts = readJson(EXPORT_COUNT_KEY, {});
  counts[reportKey] = (counts[reportKey] || 0) + 1;
  localStorage.setItem(EXPORT_COUNT_KEY, JSON.stringify(counts));
  return counts[reportKey];
}

export default function PayrollReportsWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { user, role } = useContext(AuthContext);
  const { employees } = useEmployees();

  const nav = useReportsNavigation({ payrollReports: REPORTS });

  const generatedBy = user?.name || user?.full_name || user?.email || role || "—";

  const [runId, setRunId] = useState("");
  const [runLabel, setRunLabel] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [employeeType, setEmployeeType] = useState("");
  const [runStatus, setRunStatus] = useState("");
  const [runStatusKey, setRunStatusKey] = useState("");
  const [locations, setLocations] = useState([]);
  // All employees incl. inactive: past payroll runs / exits include people who have left.
  const [allEmployees, setAllEmployees] = useState([]);
  const [fy, setFy] = useState("");
  const [calMonth, setCalMonth] = useState(currentCalMonth());
  const [departments, setDepartments] = useState([]);
  const [financialYears, setFinancialYears] = useState({ current: "", options: [] });
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "light");
  const [density, setDensity] = useState("comfortable");
  const [visibleCols, setVisibleCols] = useState(null);
  const [showColMenu, setShowColMenu] = useState(false);

  const calCtx = useMemo(() => {
    const [y, m] = (calMonth || currentCalMonth()).split("-").map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    return {
      calYear: y,
      calMonthNum: m,
      fromDate: `${y}-${String(m).padStart(2, "0")}-01`,
      toDate: `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
    };
  }, [calMonth]);
  const employeeDeptMap = useMemo(() => buildEmployeeDeptMap(employees), [employees]);

  // Branch + Employee Type scope, applied to every report.
  const scopeActive = Boolean(departmentId || locationId || employeeType);
  const matchesEmployee = useCallback(
    (emp) => {
      if (departmentId && employeeDepartmentId(emp) !== String(departmentId)) return false;
      if (locationId && employeeLocationId(emp) !== String(locationId)) return false;
      if (employeeType && normalizeEmployeeType(emp?.employee_type) !== employeeType) return false;
      return true;
    },
    [departmentId, locationId, employeeType]
  );
  const scopeIds = useMemo(() => {
    if (!scopeActive) return null;
    const pool = allEmployees.length ? allEmployees : employees;
    return new Set(pool.filter(matchesEmployee).map((e) => String(e.employee_id)));
  }, [scopeActive, allEmployees, employees, matchesEmployee]);
  const scopedEmployees = useMemo(
    () => (scopeActive ? employees.filter(matchesEmployee) : employees),
    [scopeActive, employees, matchesEmployee]
  );
  const buildCtx = useMemo(
    () => ({
      scopeIds,
      matchesEmployee: scopeActive ? matchesEmployee : null,
    }),
    [scopeIds, scopeActive, matchesEmployee]
  );

  const selectedKey = useMemo(() => {
    const fromUrl = searchParams.get("report");
    if (fromUrl && REPORTS.some((r) => r.key === fromUrl)) return fromUrl;
    return "";
  }, [searchParams]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({ generatedAt: null, durationMs: 0, bytes: 0 });

  const [gridSearch, setGridSearch] = useState("");
  const [sort, setSort] = useState({ col: null, dir: "asc" });
  const [page, setPage] = useState(1);

  const colMenuRef = useRef(null);
  const printRef = useRef(null);

  const report = useMemo(() => REPORTS.find((r) => r.key === selectedKey), [selectedKey]);
  const showGuide = !selectedKey;

  // A link report opened as ?report=<key> (old bookmark / history) goes to its own page.
  useEffect(() => {
    if (report?.isLink && report.href) navigate(report.href, { replace: true });
  }, [report, navigate]);

  // Never show the previous report's rows under a newly selected report.
  useEffect(() => {
    setRows([]);
    setError("");
  }, [selectedKey]);
  // Only show filters the selected report uses, so every visible filter has an effect.
  const usesPayrollMonth = Boolean(report?.needs.includes("month"));
  const usesCalMonth = Boolean(report?.needs.includes("calMonth"));

  useEffect(() => {
    if (!selectedKey) return;
    try {
      localStorage.setItem("prw:selectedReport", selectedKey);
    } catch (_) {} // eslint-disable-line no-empty
  }, [selectedKey]);

  useEffect(() => {
    API.get("/setup/departments").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
    API.get("/setup/locations").then((r) => setLocations(r.data || [])).catch(() => setLocations([]));
    fetchAllEmployees({ activeOnly: false }).then(setAllEmployees).catch(() => setAllEmployees([]));
    API.get("/tds/form16/financial-years")
      .then((r) => {
        setFinancialYears(r.data || { current: "", options: [] });
        setFy((prev) => prev || r.data?.current || "");
      })
      .catch(() => setFinancialYears({ current: "", options: [] }));
  }, []);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const onDoc = (e) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    setVisibleCols(null);
  }, [selectedKey, rows]);

  const selectReport = (r) => {
    // Link reports (e.g. Employee Salary Register, ESI) have their own page; their href may
    // start with "/reports/payroll/…", so check isLink rather than the path prefix.
    if (r.isLink || (r.href && !r.href.startsWith("/reports/payroll"))) {
      nav.onSelectReport(r);
      return;
    }
    if (r.key) {
      setSearchParams({ report: r.key });
      nav.pushRecent(r.key);
    }
  };

  const missingFilter = useMemo(() => {
    if (!report) return "Select a report";
    if (report.needs.includes("month") && !runId) return "Select a payroll month";
    if (report.needs.includes("calMonth") && !calMonth) return "Select a calendar month";
    return "";
  }, [report, runId, calMonth]);

  const runReport = useCallback(async () => {
    if (!report || typeof report.build !== "function" || missingFilter) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    const started = performance.now();
    try {
      const data = await report.build({
        ...buildCtx,
        runId,
        departmentId,
        employees: scopedEmployees,
        fy,
        employeeDeptMap,
        ...calCtx,
      });
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setMeta({
        generatedAt: new Date(),
        durationMs: Math.round(performance.now() - started),
        bytes: JSON.stringify(list).length,
      });
      setPage(1);
      nav.pushRecent(report.key);
    } catch (e) {
      setRows([]);
      setError(getApiErrorDetail(e) || "Failed to generate report. Please try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, missingFilter, runId, departmentId, fy, calCtx, employeeDeptMap, employees.length, buildCtx, scopedEmployees]);

  useEffect(() => {
    runReport();
  }, [runReport]);

  const columns = useMemo(() => {
    if (!rows.length) return [];
    return Object.keys(rows[0]).map((key) => ({
      key,
      num: MONEY_RE.test(key) || COUNT_RE.test(key),
      money: MONEY_RE.test(key),
    }));
  }, [rows]);

  const activeColumns = useMemo(() => {
    if (!visibleCols) return columns;
    return columns.filter((c) => visibleCols[c.key] !== false);
  }, [columns, visibleCols]);

  const filteredRows = useMemo(() => {
    if (!gridSearch.trim()) return rows;
    const q = gridSearch.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => String(v).toLowerCase().includes(q)));
  }, [rows, gridSearch]);

  const sortedRows = useMemo(() => {
    if (!sort.col) return filteredRows;
    const col = columns.find((c) => c.key === sort.col);
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = a[sort.col];
      const bv = b[sort.col];
      if (col?.num) return (parseNum(av) - parseNum(bv)) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filteredRows, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedRows, page]
  );

  const totals = useMemo(() => {
    const t = {};
    for (const c of activeColumns) {
      if (c.money) t[c.key] = inr(sortedRows.reduce((a, r) => a + parseNum(r[c.key]), 0));
    }
    return t;
  }, [activeColumns, sortedRows]);

  const toggleSort = (key) =>
    setSort((s) => (s.col === key ? { col: key, dir: s.dir === "asc" ? "desc" : "asc" } : { col: key, dir: "asc" }));

  const exportData = useCallback(
    async (kind) => {
      if (!report) return;
      if (missingFilter) {
        toast.error(missingFilter);
        return;
      }
      try {
        const data = report.buildExport
          ? await report.buildExport({
              ...buildCtx,
              runId,
              departmentId,
              employees: scopedEmployees,
              fy,
              employeeDeptMap,
              ...calCtx,
            })
          : sortedRows;
        if (!data.length) {
          toast.error("No records to export.");
          return;
        }
        if (kind === "pdf") {
          exportRowsToPdf(data, report.name, `${report.key}.pdf`);
        } else if (kind === "csv") {
          const cols = Object.keys(data[0]);
          const csv = [
            cols.join(","),
            ...data.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")),
          ].join("\n");
          // UTF-8 BOM so Excel shows the rupee sign / dashes correctly.
          downloadBlob(new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }), `${report.key}.csv`);
        } else {
          exportRowsToXlsx(data, report.name, `${report.key}.xlsx`);
        }
        bumpExportCount(report.key);
        toast.success(`${kind.toUpperCase()} export downloaded.`);
      } catch {
        toast.error("Export failed. Please try again.");
      }
    },
    [report, missingFilter, runId, departmentId, fy, calCtx, employeeDeptMap, sortedRows, toast, buildCtx, scopedEmployees]
  );

  const printReport = () => {
    if (!report || !sortedRows.length) {
      toast.error("No records to print.");
      return;
    }
    const cols = activeColumns.map((c) => c.key);
    const filterBits = [
      usesCalMonth ? `Period: ${calMonth}` : usesPayrollMonth ? `Period: ${runLabel || "\u2014"}` : null,
      `Department: ${deptName}`,
      locationId ? `Branch: ${locations.find((l) => String(l.id) === String(locationId))?.name || "\u2014"}` : null,
      employeeType ? `Employee Type: ${employeeType}` : null,
      `Rows: ${sortedRows.length}`,
      `Generated by: ${generatedBy}`,
    ].filter(Boolean);
    printRowsAsTable({ title: report.name, subtitle: filterBits.join("  |  "), columns: cols, rows: sortedRows });
  };

  const kpis = useMemo(
    () =>
      report && typeof report.kpis === "function" && rows.length
        ? report.kpis(rows, { employees: scopedEmployees })
        : [],
    [report, rows, scopedEmployees]
  );

  const showDashboard = !loading && !rows.length && !!missingFilter;

  const toggleColumn = (key) => {
    setVisibleCols((prev) => {
      const base = prev || Object.fromEntries(columns.map((c) => [c.key, true]));
      return { ...base, [key]: !base[key] };
    });
  };

  const deptName = departments.find((d) => String(d.id) === String(departmentId))?.name || "All";

  return (
    <div className="prw" ref={printRef} data-theme={theme}>
      <ReportsEnterpriseShell
        categoryOrder={nav.categoryOrder}
        reportsByCategory={nav.reportsByCategory}
        selectedKey={selectedKey}
        selectedReport={showGuide ? null : (nav.selectedReport || report)}
        reportSearch={nav.reportSearch}
        onReportSearchChange={nav.onReportSearchChange}
        favorites={nav.favorites}
        onToggleFavorite={nav.onToggleFavorite}
        recentReports={nav.recentReports}
        favReports={nav.favReports}
        onSelectReport={selectReport}
        onThemeChange={setTheme}
        theme={theme}
        moduleTitle={nav.moduleTitle}
      >
      <div className="prw__body">
        {showGuide ? (
          <ReportsGuide recentReports={nav.recentReports} onSelectReport={selectReport} />
        ) : selectedKey === "tds" ? (
          <TdsPayrollReportPanel
            generatedBy={generatedBy}
            employees={employees}
            departments={departments}
            financialYears={financialYears}
          />
        ) : (
        <div className="prw__standard">
        {/* Page header */}
        <motion.div
          className="prw__page-header"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="prw__page-header-main">
            {report && (
              <div className="prw__page-icon">
                <report.icon size={26} />
              </div>
            )}
            <div>
              <h1 className="prw__page-title">{report?.name || "Payroll Reports"}</h1>
              <p className="prw__page-desc">{report?.description}</p>
              <div className="prw__page-meta">
                {(usesPayrollMonth || usesCalMonth) && (
                  <span className="prw__meta-chip">
                    <CalendarDays size={13} />
                    Period:{" "}
                    <b>
                      {usesCalMonth ? calMonth : runLabel || (runId ? "Selected run" : "—")}
                    </b>
                  </span>
                )}
                <span className="prw__meta-chip">
                  <Users size={13} />
                  Generated by: <b>{generatedBy}</b>
                </span>
                <span className="prw__meta-chip">
                  <History size={13} />
                  Last generated:{" "}
                  <b>{meta.generatedAt ? meta.generatedAt.toLocaleString() : "—"}</b>
                </span>
              </div>
            </div>
          </div>
          {kpis.length > 0 && (
            <div className="prw__quick-stats">
              {kpis.slice(0, 3).map((k) => (
                <div key={k.label} className="prw__quick-stat">
                  <p className="prw__quick-stat-label">{k.label}</p>
                  <p className="prw__quick-stat-value">{k.value}</p>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Filter card */}
        <motion.div
          className="prw__filter-card"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.05 }}
        >
          <p className="prw__filter-card-title">
            <Filter size={16} /> Report Filters
          </p>
          <div className="prw__filter-grid">
            {usesPayrollMonth && (
            <div className="prw__filter">
              <PayrollRunSelect
                label="Payroll Month"
                value={runId}
                statusFilter={runStatus}
                onChange={(id, opt) => {
                  setRunId(id);
                  setRunLabel(opt?.label || opt?.period_label || "");
                  setRunStatusKey(opt?.statusKey || "");
                }}
                allowEmpty
                emptyLabel="Select payroll month"
                hint=""
              />
            </div>
            )}
            <div className="prw__filter">
              <label htmlFor="prw-dept">
                <Building2 size={12} /> Department
              </label>
              <select id="prw-dept" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            {usesCalMonth && (
            <div className="prw__filter">
              <label htmlFor="prw-cal">
                <CalendarDays size={12} /> Calendar Month
              </label>
              <input
                id="prw-cal"
                type="month"
                value={calMonth}
                onChange={(e) => setCalMonth(e.target.value)}
                title="Used by Attendance, Joining and Exit reports"
              />
            </div>
            )}
            <div className="prw__filter">
              <label htmlFor="prw-branch">
                <Building2 size={12} /> Branch
              </label>
              <select id="prw-branch" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                <option value="">All Branches</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="prw__filter">
              <label htmlFor="prw-emptype">
                <Users size={12} /> Employee Type
              </label>
              <select id="prw-emptype" value={employeeType} onChange={(e) => setEmployeeType(e.target.value)}>
                <option value="">All Types</option>
                {EMPLOYEE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            {usesPayrollMonth && (
            <div className="prw__filter" title="Limits the Payroll Month list to runs with this status">
              <label htmlFor="prw-runstatus">
                <CheckCircle2 size={12} /> Payroll Status
              </label>
              <select
                id="prw-runstatus"
                value={runStatus}
                onChange={(e) => {
                  const next = e.target.value;
                  setRunStatus(next);
                  // Clear a selected month whose run no longer matches the status.
                  if (next && runStatusKey && runStatusKey !== next) {
                    setRunId("");
                    setRunLabel("");
                    setRunStatusKey("");
                  }
                }}
              >
                <option value="">All Statuses</option>
                {RUN_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            )}
            <div className="prw__filter-actions">
              <button
                type="button"
                className="prw__btn prw__btn--generate"
                onClick={runReport}
                disabled={loading}
                aria-busy={loading}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                Generate
              </button>
              <button
                type="button"
                className="prw__btn prw__btn--ghost"
                onClick={() => {
                  setRunId("");
                  setRunLabel("");
                  setRunStatusKey("");
                  setDepartmentId("");
                  setLocationId("");
                  setEmployeeType("");
                  setRunStatus("");
                  setFy(financialYears.current || "");
                  setCalMonth(currentCalMonth());
                  setGridSearch("");
                  setSort({ col: null, dir: "asc" });
                }}
              >
                <RotateCcw size={16} /> Reset
              </button>
            </div>
          </div>
        </motion.div>

        {/* Action toolbar — only when filters are ready */}
        {!missingFilter && (
        <div className="prw__toolbar">
          <button type="button" className="prw__btn prw__btn--refresh prw__btn--sm" onClick={runReport} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button type="button" className="prw__btn prw__btn--excel prw__btn--sm" onClick={() => exportData("xlsx")}>
            <FileSpreadsheet size={14} /> Excel
          </button>
          <button type="button" className="prw__btn prw__btn--pdf prw__btn--sm" onClick={() => exportData("pdf")}>
            <FileText size={14} /> PDF
          </button>
          <button type="button" className="prw__btn prw__btn--csv prw__btn--sm" onClick={() => exportData("csv")}>
            <Download size={14} /> CSV
          </button>
          <button type="button" className="prw__btn prw__btn--print prw__btn--sm" onClick={printReport}>
            <Printer size={14} /> Print
          </button>

          <div className="prw__toolbar-right">
            <select
              className="prw__density-select"
              value={density}
              onChange={(e) => setDensity(e.target.value)}
              aria-label="Table density"
            >
              <option value="compact">Compact</option>
              <option value="comfortable">Comfortable</option>
              <option value="spacious">Spacious</option>
            </select>
            <div style={{ position: "relative" }} ref={colMenuRef}>
              <button
                type="button"
                className="prw__btn prw__btn--ghost prw__btn--sm"
                onClick={() => setShowColMenu((v) => !v)}
                aria-expanded={showColMenu}
              >
                <Columns3 size={14} /> Columns
              </button>
              <AnimatePresence>
                {showColMenu && columns.length > 0 && (
                  <motion.div
                    className="prw__col-menu"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                  >
                    {columns.map((c) => (
                      <label key={c.key}>
                        <input
                          type="checkbox"
                          checked={visibleCols ? visibleCols[c.key] !== false : true}
                          onChange={() => toggleColumn(c.key)}
                        />
                        {c.key}
                      </label>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="prw__grid-search">
              <Search size={14} className="prw__grid-search-icon" />
              <input
                placeholder="Search rows…"
                value={gridSearch}
                onChange={(e) => {
                  setGridSearch(e.target.value);
                  setPage(1);
                }}
                aria-label="Search report rows"
              />
            </div>
          </div>
        </div>
        )}

        {error && (
          <motion.div className="prw__error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AlertTriangle size={16} /> {error}
          </motion.div>
        )}

        <div className="prw__content">
          <div className="prw__report">
            {/* Dashboard when no data */}
            {showDashboard && (
              <motion.div
                className="prw__setup-banner"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="prw__setup-banner-icon">
                  <Table2 size={28} />
                </div>
                <div>
                  <p className="prw__setup-banner-eyebrow">
                    <Sparkles size={14} /> Get started
                  </p>
                  <h3 className="prw__setup-banner-title">Configure filters to generate {report?.name}</h3>
                  <p className="prw__setup-banner-desc">
                    {missingFilter}. Choose the required filters in the card above — the report runs automatically once a payroll month is selected, or click <strong>Generate</strong>.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Report content */}
            {loading ? (
              <div className="prw__skeleton" aria-busy="true" aria-label="Generating report">
                <div className="prw__skeleton-row" />
                <div className="prw__skeleton-row" />
                <div className="prw__skeleton-row" />
                <div className="prw__skeleton-row" />
                <div className="prw__skeleton-row" />
              </div>
            ) : !rows.length && !showDashboard ? (
              <motion.div
                className="prw__empty"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="prw__empty-illus">
                  <Inbox size={36} />
                </div>
                <h3>No records found</h3>
                <p>
                  No data matches your current filter selection. Try adjusting the payroll month,
                  department, or other filters and regenerate.
                </p>
              </motion.div>
            ) : rows.length ? (
              <motion.div
                className={`prw__grid-wrap prw__grid-wrap--${density}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <table className="prw__grid" role="grid" aria-label={report?.name}>
                  <thead>
                    <tr>
                      {activeColumns.map((c) => (
                        <th
                          key={c.key}
                          className={c.num ? "num" : ""}
                          onClick={() => toggleSort(c.key)}
                          title="Click to sort"
                          aria-sort={sort.col === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                        >
                          {c.key}
                          <span className="prw__sort">
                            {sort.col === c.key ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r, i) => (
                      <tr key={i}>
                        {activeColumns.map((c) => (
                          <td key={c.key} className={c.num ? "num" : ""}>
                            {r[c.key] ?? "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {Object.keys(totals).length > 0 && (
                    <tfoot>
                      <tr>
                        {activeColumns.map((c, idx) => (
                          <td key={c.key} className={c.num ? "num" : ""}>
                            {idx === 0 ? `Totals (${sortedRows.length})` : totals[c.key] ?? ""}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
                <div className="prw__pager">
                  <span>
                    {sortedRows.length} rows total
                  </span>
                  {totalPages > 1 && (
                    <div className="prw__pager-btns">
                      <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        Previous
                      </button>
                      <span>
                        Page {page} of {totalPages}
                      </span>
                      <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ) : null}
          </div>
        </div>
        </div>
        )}
      </div>
    </ReportsEnterpriseShell>
    </div>
  );
}
